/* eslint-disable sonarjs/no-duplicate-string */

import JSZip from 'jszip';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { BundleAssessment } from 'src/app/alert/models';
import { Question, QuestionOption, Questionnaire, QuestionnairesApi, SeverityAssessment, SeverityAssessmentResponsesInner } from 'src/app/hickory-api';
import { BundleHelperService, BundleResponse } from 'src/app/shared/bundle-helper.service';
import { AlertModelService } from '../alert-model.service';

export class AlertSeverityHelper {
  // avoid circular dependency by not importing AlertFields
  static readonly FIELD_NAME = 'severityAssessment';

  static validate(service: AlertModelService, severity: SeverityAssessment = service.alertSeverity): string[] {
    const questionsCount = service.state.questionnaire?.questions?.length || 0;
    const responseCount = severity?.responses?.length || 0;
    return responseCount > 0 && questionsCount === responseCount ? [] : ['FinishSeverityAssessment'];
  }

  // returns the Hilton(ish) questionnaire (default)
  static getDefaultQuestionnaire(): Questionnaire {
    return {
      name: 'Severity Assessment',
      questions: [
        this.createDefaultQuestion('id.hilton.assessment.questions.seriousillness', 1),
        this.createDefaultQuestion('id.hilton.assessment.questions.propertydamage', 2),
        this.createDefaultQuestion('id.hilton.assessment.questions.businessdisruption', 3),
        this.createDefaultQuestion('id.hilton.assessment.questions.evacuation', 4),
        this.createDefaultQuestion('id.hilton.assessment.questions.mediainterest', 5)
      ]
    };
  }

  private static createDefaultQuestion(id: string, sequence: number): Question {
    return <Question>{
      id,
      sequence,
      text: this.mapQuestion({ questionId: id }, null),
      options: [
        {
          id: 'id.hilton.assessment.responses.yesno.yes',
          text: this.mapOption({ optionId: 'id.hilton.assessment.responses.yesno.yes' }, null),
          sequence: 1
        },
        {
          id: 'id.hilton.assessment.responses.yesno.no',
          text: this.mapOption({ optionId: 'id.hilton.assessment.responses.yesno.no' }, null),
          sequence: 2
        }
      ]
    }
  }

  static mapQuestion(severity: (SeverityAssessmentResponsesInner | Partial<SeverityAssessmentResponsesInner>), assessmentQuestions: Question[]): string {
    switch (severity.questionId) {
      case 'id.hilton.assessment.questions.seriousillness':
      case 'id_hilton_assessment_questions_seriousillness':
        return 'Was there serious illness, injury, or loss of life?';
      case 'id.hilton.assessment.questions.propertydamage':
      case 'id_hilton_assessment_questions_propertydamage':
        return 'Was there significant damage or loss?';
      case 'id.hilton.assessment.questions.businessdisruption':
      case 'id_hilton_assessment_questions_businessdisruption':
        return 'Was or do you expect there to be a significant business disruption?';
      case 'id.hilton.assessment.questions.evacuation':
      case 'id_hilton_assessment_questions_evacuation':
        return 'Was there a partial or full evacuation of the property?';
      case 'id.hilton.assessment.questions.mediainterest':
      case 'id_hilton_assessment_questions_mediainterest':
        return 'Were there or do you anticipate media inquiries or external interest?';
      default: {
        if (assessmentQuestions) {
          for (const q of assessmentQuestions) {
            if (q.id === severity.questionId) {
              return q.text;
            }
          }
        }

        return severity.question;
      }
    }
  }

  static mapOption(severity: (SeverityAssessmentResponsesInner | Partial<SeverityAssessmentResponsesInner>), assessmentQuestions: Question[]): string {
    switch (severity.optionId) {
      case 'id.hilton.assessment.responses.yesno.yes':
      case 'id_hilton_assessment_responses_yesno_yes':
        return 'Yes';
      case 'id.hilton.assessment.responses.yesno.no':
      case 'id_hilton_assessment_responses_yesno_no':
        return 'No';
      default: {
        if (assessmentQuestions) {
          for (const q of assessmentQuestions) {
            for (const o of q.options) {
              if (o.id === severity.optionId) {
                return o.text;
              }
            }
          }
        }

        return severity.option;
      }
    }
  }

  static createQuestionnaireFromBundle(assessment: BundleAssessment): Questionnaire {
    return <Questionnaire>{
      name: 'Severity Assessment',
      questions: this.createQuestionsFromSeverityAssessment(assessment)
    }
  }

  static createQuestionsFromSeverityAssessment(assessment: BundleAssessment): Question[] {
    const questions: Question[] = [];
    // map options and response so we can easily get them by their ids
    let optionsMap: { [key: string]: any };
    for (const option of assessment.options) {
      optionsMap = {
        ...optionsMap,
        [option.id]: option
      }
    }

    let responsesMap: { [key: string]: any };
    for (const response of assessment.responses) {
      responsesMap = {
        ...responsesMap,
        [response.questionId]: response
      }
    }

    for (let i = 0; i < assessment.questions.length; i++) {
      const question = assessment.questions[i];
      let optionsKey = responsesMap[question.id].optionId;
      if (optionsKey.startsWith("id.hilton.assessment.responses.yesno")) {
        optionsKey = "id.hilton.assessment.responses.yesno";
      }
      questions.push(<Question>{
        id: question.id,
        text: question.question,
        legacyId: question.id,
        options: this.buildQuestionFromSeverityOptionAndResponse(optionsMap[optionsKey])
      })
    }
    return questions;
  }

  private static buildQuestionFromSeverityOptionAndResponse(option: any): QuestionOption[] {
    const questionOptions: QuestionOption[] = [];
    for (let i = 0; i < option.options.length; i++) {
      const assessmentOption = option.options[i];
      const questionOption: QuestionOption =
      {
        id: assessmentOption.id,
        legacyId: assessmentOption.id,
        text: assessmentOption.option,
        sequence: i,
      };
      questionOptions.push(questionOption);
    }
    return questionOptions;
  }

  /**
   * Finds the correct severity assessment to provide to the user. 
   * * If the api returns no response, we will check the bundle 
   * * If there is no bundle assessment, the default is returned
   * * bundle service and feature toggle service as parameters to avoid complexity of yet another service and to avoid circular dependencies
   * 
   * @param apiResponse takes in an observable to allow for customization with rxjs when this method is subscribed to
   * @param featureToggle (used for localhost only)
   * @param bundleHelper
   * @returns an observable of a questionnaire
   */
  static getQuestionnaireAsync(api: QuestionnairesApi, bundleHelper: BundleHelperService): Observable<Questionnaire> {
    return new Observable<Questionnaire>((subscriber) => {
      api.getQuestionnaires(1, 1).subscribe({
        next: (res) => {
        // if the api returns a response use the questionnaire
          if (res?.items?.length > 0 && res?.items[0]?.questions?.length > 0) {
            subscriber.next(res.items[0]);
            subscriber.complete();
          } else {
            this.loadSeverityFromBundleResponse(bundleHelper).subscribe({
              next: (bundleRes) => {
                subscriber.next(bundleRes);
                subscriber.complete();
              },
            });
          }
        },
        error: (err) => {
          subscriber.error(err);
          subscriber.complete();
        }
      })
    });
  }

  private static loadSeverityFromBundleResponse(bundleHelper: BundleHelperService): Observable<Questionnaire> {
    return bundleHelper
      .loadBundle(/^bundle\/SeverityAssessment\/.{2,5}\/$/i, 'bundle/SeverityAssessment', 'EN')
      .pipe(
        catchError(_err => {
          return of(<BundleResponse>{});
        }),
        switchMap((response: BundleResponse) => {
          return this.getBundleQuestionnaire(response.basePath, response.zipFile)
        })
      );
  }

  private static getBundleQuestionnaire(path: string, zipFile: JSZip): Promise<Questionnaire> {
    // bundle/SeverityAssessment/EN/SeverityAssessmentQuestions.json
    const filePath = path + 'SeverityAssessmentQuestions.json';
    const files = zipFile?.file(filePath);

    if (!files) {
      return Promise.resolve(AlertSeverityHelper.getDefaultQuestionnaire());
    }

    return files.async('text')
      .then((text: string) => {
        return this.createQuestionnaireFromBundle(<BundleAssessment>JSON.parse(text))
      }, (_error: any) => {
        return AlertSeverityHelper.getDefaultQuestionnaire();
      });
  }
}
